import * as Sentry from "@sentry/react"
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query"

import { ApiError } from "@/lib/api"
import { notifyTransientFailure, onBackendRecovered } from "@/lib/backend-status"
import { isAccountPendingDeletionError, isMembershipInactiveError } from "@/lib/me-api"

/**
 * Report a failed query/mutation to Sentry.
 *
 * TanStack Query "handles" every failure by surfacing it via `error` / `onError`, so it
 * never bubbles as an unhandled error — without this it never reaches Sentry, even though
 * the UI shows a toast. Cache-level `onError` fires once per request and cannot be
 * overridden by a per-call `onError`, so each failure is reported exactly once while local
 * toasts stay untouched. This is the recommended place for monitoring (see TkDodo,
 * "React Query Error Handling"). `key` (queryHash / mutationKey) fingerprints so repeats
 * of the same request group into one Sentry issue instead of splintering by stack trace.
 */
function reportToSentry(error: unknown, source: "query" | "mutation", key: string | undefined): void {
  // Deactivated-membership 403 (FLO-166) is an expected app state with a dedicated screen — not an error worth a Sentry issue.
  if (isMembershipInactiveError(error)) return
  // Pending-account-deletion 403 (FLO-185) — same idea: everyone is locked out to a dedicated screen, not a bug.
  if (isAccountPendingDeletionError(error)) return
  Sentry.withScope((scope) => {
    scope.setTag("react_query.source", source)
    if (key) {
      scope.setFingerprint(["react-query", source, key])
    }
    if (error instanceof ApiError) {
      // Tag the HTTP status so expected client errors (401/403/422) can be filtered in Sentry.
      scope.setTag("http.status", String(error.status))
      scope.setContext("api_error", { status: error.status, data: error.data })
    }
    Sentry.captureException(error)
  })
}

/** Defaults tuned for dashboard-style data: fewer surprise refetches, sane GC. */
export const queryClient = new QueryClient({
  // Global error reporting for everything going through TanStack Query. Per-call `onError`
  // handlers (toasts, optimistic rollback) still run — these fire in addition, not instead.
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Transient failure (502+/network) → verify /health and, if it's down too, show the
      // full-screen reconnecting hold (see backend-status.ts). Real errors pass through.
      notifyTransientFailure(error)
      reportToSentry(error, "query", query.queryHash)
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      notifyTransientFailure(error)
      reportToSentry(error, "mutation", mutation.options.mutationKey?.join("/"))
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false, // don't refetch on window focus
      refetchOnReconnect: true, // refetch on reconnect
      // Function form (not `retry: 1`) so the pending-account-deletion 403 (FLO-185) is never
      // retried — it won't resolve by retrying, and in-flight queries should surface it
      // immediately so AuthenticatedLayout can swap to the locked screen. Every other error keeps
      // the previous behavior: retry once.
      retry: (failureCount, error) => {
        if (isAccountPendingDeletionError(error)) return false
        return failureCount < 1
      },
    },
    mutations: {
      retry: 0,
    },
  },
})

// Backend came back after a deploy/outage: refetch everything (including errored queries) so
// the page the user was on fills back in — recovery without a page reload.
onBackendRecovered(() => {
  void queryClient.invalidateQueries()
})
